import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import {
  get,
  getPhoenixApiTenantUrl,
  type Api,
} from '@pflegenavi/shared-frontend/platform';
import { Filter } from '../filter';
import { addDays, formatISO, startOfDay } from 'date-fns';

interface CashList {
  id: string;
  nursing_home_id: string;
}

interface CashListTransactionGroupList {
  id: string;
  group_type: string;
  mismatch_confirmation_timestamp: string | null;
  amount_linked: number;
  linking_difference: number;
  separate_fee_amount_linked: number;
  separate_fee_amount_linking_difference: number;
  immediate_fee_amount_linked: number;
  immediate_fee_amount_linking_difference: number;
  cash_changed: number;
  separate_fee_amount: number;
  immediate_fee_amount: number;
  reversed: boolean;
  cash_list: CashList;
  open_receipt_count: number;
  open_receipt_batch_count: number;
  expense?: {
    id: string;
    receipt_date: string | null;
    expense_parts: Array<{ id: string; amount: number }>;
  } | null;
}

export interface CashListTransaction {
  id: string;
  cash_list_id: string;
  type: string;
  status: string;
  amount: number;
  separate_fee_amount: number;
  separate_fee_percentage: string | null;
  immediate_fee_amount: number;
  immediate_fee_tracked: boolean;
  notes: string | null;
  coin_change: number | null;
  bank_account_amount_change: number;
  group_id: string;
  full_reversal: boolean;
  inserted_at: string;
  event_datetime: string;
  employee_id: string;
  employee?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  group?: {
    id: string;
    group_type: string;
  };
  group_list: CashListTransactionGroupList;
  reversed: boolean;
  pliant_transaction_id?: string;
  pliant_transaction?: {
    pliant_transaction_id: string;
    pliant_type: string;
    pliant_status: string;
    billing_amount: number;
  };
  balance_before: number;
  balance_after: number;
  related_residents: Array<{
    id: string;
    firstname: string;
    lastname: string;
  }>;
}

export interface TransactionCountry {
  id: string;
  name: string;
  iso_code_3: string;
  iso_code: string;
  numeric_code: number;
}

export interface PliantCardholder {
  pliant_cardholder_id: string;
  pliant_organization_id: string;
  pliant_cardholder_status: string;
  employee: Employee;
}

export interface Employee {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  email_verified: boolean | null;
}

export interface PliantOrganization {
  id: string;
  iban_last_4: string | null;
  mangopay_mandate_id: string | null;
  mangopay_user_id: string;
  pliant_organization_id: string;
  mangopay_wallet_id: string;
  base_data: any | null;
  mangopay_kyc_verified: boolean;
  mangopay_bank_account_id: string | null;
  mangopay_mandate_status: string | null;
  company_number: string;
  legal_person_type: string;
  legal_representative_base_data: any | null;
  mangopay_pliant_bank_account_id: string | null;
  payout_aggregation: string;
  payout_day_of_week: string | null;
  pliant_iban_last_4: string | null;
}

export interface PliantTransaction {
  transaction_country: TransactionCountry;
  cash_list_transactions: CashListTransaction[];
  billing_amount: number;
  pliant_status: PliantStatus;
  pliant_type:
    | 'PURCHASE'
    | 'CASH_WITHDRAWAL'
    | 'REFUND'
    | 'CHARGEBACK'
    | 'RECHARGE'
    | 'STATUS_INQUIRY';
  booked_at: Date;
  pliant_transaction_id: string;
  pliant_card_account_id: string;
  pliant_card_id: string;
  pliant_category:
    | 'ADVERTISING_AND_MARKETING'
    | 'COMPUTING_AND_SOFTWARE'
    | 'EDUCATION_AND_TRAINING'
    | 'ELECTRONICS_AND_IT_EQUIPMENT'
    | 'ENTERTAINMENT_AND_WELLNESS'
    | 'FOOD_AND_DRINKS'
    | 'GIFTS_AND_VOUCHERS'
    | 'MATERIALS_AND_PACKAGING'
    | 'OFFICE_SUPPLIES_AND_EQUIPMENT'
    | 'SERVICES'
    | 'TRAVEL_AND_ACCOMMODATION'
    | 'HEALTHCARE'
    | 'OTHER';
  created_at: Date;
  authorized_at: Date;
  confirmed_at: Date;
  reversed_at: Date | null;
  pliant_merchant_id: string | null;
  merchant_name: string;
  mcc: string;
  merchant_terminal_id: string;
  inserted_at: string;
  mobile_wallet: string | null;
  updated_at: string;
  merchant_base_data: any | null;
  pliant_cardholder: PliantCardholder;
  pliant_organization: PliantOrganization;
}

export enum PliantStatus {
  DECLINED = 'DECLINED',
  PENDING = 'PENDING',
  REVERSED = 'REVERSED',
  CONFIRMED = 'CONFIRMED',
  BOOKED = 'BOOKED',
}

export enum PliantType {
  PURCHASE = 'PURCHASE',
  CASH_WITHDRAWAL = 'CASH_WITHDRAWAL',
  REFUND = 'REFUND',
  RECHARGE = 'RECHARGE',
  STATUS_INQUIRY = 'STATUS_INQUIRY',
  CHARGEBACK = 'CHARGEBACK',
}

export interface GetPliantTransactionParams {
  params: {
    pliant_transaction_id: string;
    legal_entity_id: string;
  };
}

export interface GetPliantTransactionsParams {
  params: {
    page: number;
    page_size: number;
    legal_entity_id: string;
    nursing_home_id?: string;
    mismatch?: boolean;
    has_assigned_transaction_group?: boolean;
    employee_ids?: string[];
    pliant_status?: PliantStatus[];
    pliant_type?: PliantType[];
    created_at_from?: Date;
    created_at_to?: Date;
    booked_at_from?: Date;
    booked_at_to?: Date;
  };
}

export interface GetPliantTransactionsResponse {
  data: PliantTransaction[];
  meta: {
    total_count: number;
    total_pages: number;
    current_page: number;
    previous_page: number | null;
    next_page: number | null;
    has_next_page: boolean;
    has_previous_page: boolean;
    page_size: number;
  };
}

export enum PliantCardType {
  PHYSICAL = 'PHYSICAL',
  VIRTUAL = 'VIRTUAL',
}

export interface PliantCard {
  pliant_card_id: string;
  pliant_organization_id: string;
  pliant_cardholder: {
    pliant_cardholder_id: string;
    pliant_email: string | null;
    employee: {
      id: string;
      first_name: string;
      last_name: string;
    } | null;
  } | null;
  card_type: PliantCardType;
  card_status: string;
  last_four_digits: string | null;
  issued_at: string | null;
  expires_at: string | null;
  terminated_at: string | null;
  transaction_limit: number;
  transaction_limit_currency: string;
  spending_balance: number;
  spending_balance_currency: string;
  available_balance: number;
  available_balance_currency: string;
  total_limit: number;
  total_limit_currency: string;
  limit_period_end: string | null;
  limit_renew_date: string | null;
  limit_renew_frequency: string | null;
}

export interface GetPliantCardsParams {
  params: {
    page?: number;
    page_size?: number;
    legal_entity_id?: string;
    /** Only cards held by an employee active in this nursing home. */
    nursing_home_id?: string;
    card_type?: PliantCardType;
    /** true: only cards in a final status (terminated, expired, rejected). false: only live cards. */
    final?: boolean;
  };
}

export interface GetPliantCardsResponse {
  data: PliantCard[];
  meta: {
    total_count: number;
    total_pages: number;
    current_page: number;
    previous_page: number | null;
    next_page: number | null;
    has_next_page: boolean;
    has_previous_page: boolean;
    page_size: number;
  };
}

export interface IPliantPhoenixApi extends Api {
  getPliantTransactions(
    params: GetPliantTransactionsParams
  ): Promise<GetPliantTransactionsResponse>;

  getPliantTransaction(
    params: GetPliantTransactionParams
  ): Promise<{ data: PliantTransaction }>;

  getPliantCards(params: GetPliantCardsParams): Promise<GetPliantCardsResponse>;
}

export class PliantPhoenixApi implements IPliantPhoenixApi {
  baseUrl: string;

  getPliantTransactions: IPliantPhoenixApi['getPliantTransactions'];
  getPliantTransaction: IPliantPhoenixApi['getPliantTransaction'];
  getPliantCards: IPliantPhoenixApi['getPliantCards'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getPliantTransactions = get({
      authContext,
      url: (params) => {
        const filter = new Filter();

        if (params?.has_assigned_transaction_group !== undefined) {
          filter.field(
            'assigned_transaction_group_id',
            'not_empty',
            params.has_assigned_transaction_group ? 'true' : 'false'
          );
        }

        if (params?.employee_ids !== undefined) {
          filter.field('employee_id', 'in', params.employee_ids);
        }

        if (params?.pliant_status !== undefined) {
          filter.field('pliant_status', 'in', params.pliant_status);
        }

        if (params?.pliant_type !== undefined) {
          filter.field('pliant_type', 'in', params.pliant_type);
        }

        if (params?.mismatch) {
          filter.field('mismatch', '==', 'true');
        }

        filter.fieldIfPresent(
          'created_at',
          '>=',
          params?.created_at_from?.toISOString()
        );
        // created_at is a datetime: compare against the start of the next
        // day so the last second of the day is not cut off.
        filter.fieldIfPresent(
          'created_at',
          '<',
          params?.created_at_to &&
            startOfDay(addDays(params.created_at_to, 1)).toISOString()
        );
        // booked_at is a date column, so a datetime would fail the cast.
        filter.fieldIfPresent(
          'booked_at',
          '>=',
          params?.booked_at_from &&
            formatISO(params.booked_at_from, { representation: 'date' })
        );
        filter.fieldIfPresent(
          'booked_at',
          '<=',
          params?.booked_at_to &&
            formatISO(params.booked_at_to, { representation: 'date' })
        );

        const query = filter.build();

        query.append('page', params.page.toString());
        query.append('page_size', params.page_size.toString());

        if (params?.nursing_home_id !== undefined) {
          query.append('nursing_home_id', params.nursing_home_id);
        }

        query.append('order_by[]', 'created_at');
        query.append('order_directions[]', 'desc');

        return `${this.baseUrl}/legal-entity/${
          params.legal_entity_id
        }/pliant-transactions?${query.toString()}`;
      },
    });

    this.getPliantTransaction = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/legal-entity/${params.legal_entity_id}/pliant-transactions/${params.pliant_transaction_id}`,
    });

    this.getPliantCards = get({
      authContext,
      url: (params) => {
        const filter = new Filter()
          .fieldIfPresent('legal_entity_id', '==', params.legal_entity_id)
          .fieldIfPresent('nursing_home_id', '==', params.nursing_home_id)
          .fieldIfPresent('card_type', '==', params.card_type);

        // Boolean, so `false` must still be sent; fieldIfPresent would drop it.
        if (params.final !== undefined) {
          filter.field('final', '==', params.final.toString());
        }

        const query = filter.build();

        // Counting is done through meta.total_count, so a caller that only wants
        // a count asks for page_size 1 and reads the metadata.
        query.append('page', (params.page ?? 1).toString());
        query.append('page_size', (params.page_size ?? 50).toString());

        return `${this.baseUrl}/pliant-card?${query.toString()}`;
      },
    });
  }
}
